<template>
    <form
        class="mb-5 flex flex-col"
        @submit.prevent="onSearchPhraseChange"
    >
        <div class="flex md:flex-col lg:flex-row md:flex-wrap bg-panel border py-2 md:py-3 px-4 md:rounded-lg gap-3">
            <div class="flex grow xl:max-w-80">
                <InputField
                    v-model="phrase"
                    :placeholder="$t('search_documents_tab.placeholder')"
                    icon="icon-search"
                    autocomplete="off"
                    name="phrase"
                    clickable-icon
                    class="search-field min-w-40 w-full"
                    @click-icon="!maxSM && onSearchPhraseChange()"
                    @click="maxSM && openModal()"
                />
            </div>

            <div class="grow-[2] flex gap-2 md:gap-3 max-w-full justify-end">
                <InputFilter
                    v-if="!maxSM"
                    ref="courseFilter"
                    class="border flex-shrink"
                    icon="course"
                    can-edit
                    :filter-name="$t('filters.course_title')"
                    :value-name="course && course.name"
                    @open="openCourseSearch"
                    @clear="$emit('update:course', null)"
                >
                    <CourseSearch
                        ref="courseSearch"
                        :model-value="course"
                        class="course-search !min-w-[14rem] !text-slate-400 !font-normal"
                        :university-id="currentUniversity.id"
                        :placeholder="$t('filters.course_title')"
                        @input="updateCourse"
                    />
                </InputFilter>
                <UniversityShowMoreOptions
                    v-if="!maxSM"
                    class="more-filters"
                />
                <BtnMenu
                    variant="outlined"
                    :label="sortingLabels[sortBy]"
                    :options="sortingOptions"
                    icon="sorting"
                    size="lg"
                    class="whitespace-nowrap"
                />
            </div>
        </div>
        <BtnCta
            v-show="!maxSM && isAnyFilterAdded"
            variant="tertiary"
            :label="$t('filter.clear_all_filters')"
            class="self-end m-2 -mb-3"
            @click="clearFilters"
        />
    </form>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import InputFilter from '@components/Filter/InputFilter.vue';
import CourseSearch from '@components/Select/CourseSearch.vue';
import { useScreenSize } from '@/composables/screenSize';
import UniversityShowMoreOptions from './UniversityShowMoreOptions.vue';

export default {
    name: 'UniversityShowDocumentsFilter',

    components: {
        UniversityShowMoreOptions,
        CourseSearch,
        InputFilter,
    },

    props: {
        course: {
            type: Object,
            default: null,
        },
    },
    emits: ['update:course'],

    i18nOptions: {
        namespaces: ['university', 'search'],
    },

    setup() {
        const { maxSM } = useScreenSize();
        return {
            maxSM,
        };
    },

    data() {
        return {
            phrase: '',
        };
    },

    computed: {
        ...mapGetters({
            currentUniversity: 'university/currentUniversity',
            searchParams: 'university/searchParams',
        }),
        isAnyFilterAdded() {
            return this.searchParams.course_id || this.searchParams.document_type?.length;
        },
        sortBy() {
            return this.searchParams.sort_type_documents || 'downloads';
        },
        sortingLabels() {
            return {
                relevance: this.$t(`sorting.best_match`),
                downloads: this.$t(`sorting.downloads`),
                rating: this.$t(`sorting.rating`),
                uploaded_at: this.$t(`sorting.newest`),
            };
        },
        sortingOptions() {
            return Object.entries(this.sortingLabels).map(([key, label]) => ({
                label,
                onClick: () => this.onSortChange(key),
            }));
        },
    },

    watch: {
        searchParams: {
            deep: true,
            immediate: true,
            handler: function searchParamsWatcher(params) {
                this.phrase = params.phrase || '';
            },
        },
    },

    methods: {
        async openCourseSearch() {
            await this.$nextTick();
            this.$refs.courseSearch.focusInput();
        },
        updateCourse(course) {
            this.$emit('update:course', course);
            if (course?.name) {
                this.$refs.courseFilter.close();
            }
        },
        onSearchPhraseChange() {
            return this.$store.dispatch('university/applyParams', {
                phrase: this.phrase,
            });
        },
        onSortChange(sort) {
            this.$store.dispatch('university/applyParams', {
                sort_type_documents: sort,
                p: 1,
            });
        },
        clearFilters() {
            this.$store.dispatch('university/applyParams', {
                course_id: null,
                document_type: [],
            });
            this.$refs.courseFilter.clear();
        },
        async openModal() {
            const data = await this.$modal.open(
                defineAsyncComponent(() => import('./UniversityShowSearchFilterModal.vue')),
                {
                    course: this.course,
                    documentType: this.searchParams.document_type,
                },
            );

            if (!data) return;

            this.$store.dispatch('university/applyParams', {
                course_id: data.course?.id,
                document_type: data.documentType,
                phrase: data.phrase,
            });

            this.$emit('update:course', data.course);
        },
    },
};
</script>
<style lang="scss" scoped>
button {
    outline: 0px dotted;
}

.course-search {
    min-width: 318px;
}
</style>
